
import { MetalPrice } from './types';

export interface ChartDataPoint {
  timestamp: number;
  date: string;
  gold?: number;
  silver?: number;
}

export const processAPIData = (rawData: any, type: 'spot' | 'fixing' | 'storico') => {
  if (!rawData) {
    throw new Error('Invalid API data: received null or undefined');
  }

  // Gestisci multipli livelli di nesting:
  // Caso 1: {success: true, data: {status: "success", data: {XAU:...}}}
  // Caso 2: {status: "success", data: {XAU:...}}
  // Caso 3: {XAU:..., XAG:..., oro:..., argento:...}
  let sourceData = rawData.data || rawData;
  
  // Se c'è ancora un wrapper "status"/"data", unwrap di nuovo
  if (sourceData.status && sourceData.data) {
    sourceData = sourceData.data;
  }

  if (type === 'spot' || type === 'fixing') {
    // Supporta sia formato XAU/XAG (API diretta) che oro/argento (fixing emulato)
    const goldData = sourceData.XAU || sourceData.oro;
    const silverData = sourceData.XAG || sourceData.argento;
    
    if (!goldData || !silverData) {
      throw new Error(`Invalid ${type} price data format. Expected XAU/XAG or oro/argento properties.`);
    }
    
    const isEmulated = sourceData._emulated === true;
    
    return {
      gold: {
        price: parseFloat(goldData.prezzo || '0'),
        change: parseFloat(goldData.variazione_g || '0'),
        changePercentage: parseFloat(goldData.variazione_percentuale || '0'),
        timestamp: goldData.data_ora || '',
        fixingPrice: goldData.fixing_prezzo ? parseFloat(goldData.fixing_prezzo) : undefined,
        fixingDate: goldData.fixing_data,
        isEmulated
      },
      silver: {
        price: parseFloat(silverData.prezzo || '0'),
        change: parseFloat(silverData.variazione_g || '0'),
        changePercentage: parseFloat(silverData.variazione_percentuale || '0'),
        timestamp: silverData.data_ora || '',
        fixingPrice: silverData.fixing_prezzo ? parseFloat(silverData.fixing_prezzo) : undefined,
        fixingDate: silverData.fixing_data,
        isEmulated
      },
    };
  } else { // 'storico'
    if (!Array.isArray(sourceData)) {
      throw new Error('Historical data should be an array');
    }
    const goldHistory = sourceData
      .filter((item: any) => item && item.XAU && !isNaN(parseFloat(item.XAU)))
      .map((item: any): Partial<MetalPrice> => ({
        price: parseFloat(item.XAU),
        timestamp: item.periodo || '',
        change: 0,
        changePercentage: 0,
      }));
      
    const silverHistory = sourceData
      .filter((item: any) => item && item.XAG && !isNaN(parseFloat(item.XAG)))
      .map((item: any): Partial<MetalPrice> => ({
        price: parseFloat(item.XAG),
        timestamp: item.periodo || '',
        change: 0,
        changePercentage: 0,
      }));
      
    return {
      goldHistory,
      silverHistory
    };
  }
};

export const processChartData = (
  goldHistory: Partial<MetalPrice>[],
  silverHistory: Partial<MetalPrice>[]
): ChartDataPoint[] => {
  if (!goldHistory?.length && !silverHistory?.length) {
    return [];
  }

  const dataMap = new Map<string, Partial<ChartDataPoint>>();

  const processHistory = (history: Partial<MetalPrice>[], metal: 'gold' | 'silver') => {
    history.forEach(point => {
      if (point.timestamp && point.price) {
        const date = new Date(point.timestamp);
        const dateKey = date.toISOString().split('T')[0]; // Group by day
        const entry = dataMap.get(dateKey) || { timestamp: date.getTime(), date: date.toLocaleDateString() };
        
        if (metal === 'gold') {
          entry.gold = point.price;
        } else {
          entry.silver = point.price;
        }
        
        dataMap.set(dateKey, entry);
      }
    });
  };

  processHistory(goldHistory, 'gold');
  processHistory(silverHistory, 'silver');
  
  const sortedData = Array.from(dataMap.values()).sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));

  return sortedData.map(d => ({
    timestamp: d.timestamp || 0,
    date: d.date || '',
    gold: d.gold,
    silver: d.silver
  }));
};
